import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, ArrowCounterClockwise, Export, FileCsv, Plus, WarningCircle } from '@phosphor-icons/react';
import {
  AnsichtUmschalter, BestaetigenDialog, FlipCard, ImmoButton, ListenSuche, ObjektFilter, SegmentedControl, Seitennavigation, ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import { heuteIso } from '../../../../lib/immobilien/format';
import { mitViewTransition } from '../../../../lib/immobilien/viewTransition';
import { objektOptionen } from '../../../../lib/immobilien/objektOptionen';
import { seiteAus } from '../../../../lib/immobilien/seiten';
import {
  basisZeilen, baueEigZeilen, eigKennzahlen, eigentuemerJeObjekt, istRollenFilter, istStatusFilter, naechsteWechsel, passtRolle, passtStatus,
  ROLLEN_FILTER, STATUS_FILTER, zaehler, zuordnungsDaten, type RollenFilter, type StatusFilter,
} from '../../../../lib/eigentuemer/liste';
import { useEigentuemerListe } from '../../../../lib/eigentuemer/useEigentuemerListe';
import type { ObjektKurz } from '../../../../lib/eigentuemer/csvImport';
import type { EigExportRoh } from '../../../../lib/eigentuemer/export';
import { NeuerEigentuemerFormular } from '../neu/NeuerEigentuemerFormular';
import { ImportPanel } from './ImportPanel';
import { ExportPanel } from './ExportPanel';
import { EigentuemerWidgets } from './EigentuemerWidgets';
import { EigentuemerKachel } from './EigentuemerKachel';
import { EigentuemerTabelle } from './EigentuemerTabelle';
import PortalSchalterKarte from '../../../../components/portal/PortalSchalterKarte';
import BenachrichtigungKarte from '../../../../components/portal/BenachrichtigungKarte';

/** Eigentümer je Seite (wie Mieterliste). */
const PRO_SEITE = 20;
const KOPF_STIL: React.CSSProperties = { boxShadow: 'inset 0 0 0 1px var(--line)', fontWeight: 600 };

export interface EigentuemerListeSeiteProps {
  initialEigentuemerId?: string | null;
  onClearInitial?: () => void;
}

export default function EigentuemerListeSeite({ initialEigentuemerId, onClearInitial }: EigentuemerListeSeiteProps) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const heute = heuteIso();
  const { zustand, neuLaden } = useEigentuemerListe();
  const [suche, setSuche] = useState('');
  // Panel über der Liste (Handoff §6.2/§6.3)
  const [listenPanel, setListenPanel] = useState<'import' | 'export' | null>(null);
  const [formularGeaendert, setFormularGeaendert] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);
  const [toast, zeige] = useToastPill();
  const formularOffen = params.get('neu') === '1';
  const listeOben = useRef<HTMLDivElement>(null);

  const status: StatusFilter = istStatusFilter(params.get('f')) ? params.get('f') as StatusFilter : 'aktiv';
  const rolle: RollenFilter = istRollenFilter(params.get('rf')) ? params.get('rf') as RollenFilter : 'alle';
  const objekt = params.get('obj') ?? 'alle';
  const ansicht = params.get('view') === 'liste' ? 'liste' : 'kacheln';

  const setzeParam = useCallback((schluessel: string, wert: string, standard: string) => {
    setParams(p => {
      const n = new URLSearchParams(p);
      if (wert === standard) n.delete(schluessel); else n.set(schluessel, wert);
      if (schluessel !== 'seite') n.delete('seite');
      return n;
    });
  }, [setParams]);
  const sucheAendern = (v: string) => {
    setSuche(v);
    if (params.get('seite')) setParams(p => { const n = new URLSearchParams(p); n.delete('seite'); return n; }, { replace: true });
  };

  const daten = zustand.status === 'ok' ? zustand.daten : null;
  const zeilen = useMemo(() => {
    if (!daten) return [];
    return baueEigZeilen({
      eigentuemer: daten.eigentuemer, objektEig: daten.objektEig, einheitEig: daten.einheitEig,
      daten: zuordnungsDaten(daten.objekte, daten.einheiten, daten.mieter, heute),
      // Eigentümer- und Hausgeldkonten starten mit der Buchhaltung (gemeinsames Mietkonto)
      portal: daten.portal, salden: null, heute, verguetungen: daten.verguetungen,
    });
  }, [daten, heute]);

  const k = useMemo(() => eigKennzahlen(zeilen, false), [zeilen]);
  const wechsel = useMemo(() => naechsteWechsel(zeilen, heute), [zeilen, heute]);
  const optionen = useMemo(() => (daten ? objektOptionen(daten.objekte, eigentuemerJeObjekt(zeilen)) : []), [daten, zeilen]);
  const basis = useMemo(() => basisZeilen(zeilen, objekt, suche), [zeilen, objekt, suche]);
  const z = useMemo(() => zaehler(basis, status, rolle), [basis, status, rolle]);
  const sichtbar = useMemo(() => basis.filter(x => passtStatus(x, status) && passtRolle(x, rolle)), [basis, status, rolle]);
  const alleAnzahl = useMemo(() => zeilen.filter(x => x.status !== 'ehemalig').length, [zeilen]);

  const seiteRoh = Number(params.get('seite') ?? '1');
  const seite = useMemo(() => seiteAus(sichtbar, seiteRoh, PRO_SEITE), [sichtbar, seiteRoh]);
  const zuSeite = useCallback((n: number) => {
    setzeParam('seite', String(n), '1');
    listeOben.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [setzeParam]);

  const oeffne = useCallback((id: string) => {
    mitViewTransition(() => {
      navigate(`/admin/eigentuemer/${encodeURIComponent(id)}`);
      window.scrollTo(0, 0);
    });
  }, [navigate]);

  useEffect(() => {
    if (!initialEigentuemerId) return;
    navigate(`/admin/eigentuemer/${encodeURIComponent(initialEigentuemerId)}`);
    onClearInitial?.();
  }, [initialEigentuemerId, navigate, onClearInitial]);

  // Drehkarte „Neuer Eigentümer“ (Handoff §6.4); öffnen schließt das Import-/Export-Panel (§9)
  const schliesseFormular = useCallback(() => {
    setVerwerfen(false);
    setFormularGeaendert(false);
    setParams(p => { const n = new URLSearchParams(p); n.delete('neu'); return n; });
  }, [setParams]);
  const neuerEigentuemer = () => {
    if (!formularOffen) {
      setListenPanel(null);
      setParams(p => { const n = new URLSearchParams(p); n.set('neu', '1'); return n; });
      return;
    }
    if (formularGeaendert) setVerwerfen(true); else schliesseFormular();
  };
  const gespeichert = useCallback((_id: string, meldung: string) => {
    setFormularGeaendert(false);
    setSuche('');
    // zurückdrehen und die Liste mit allen aktiven Eigentümern zeigen
    setParams(p => { const n = new URLSearchParams(p); ['neu', 'f', 'rf', 'obj', 'seite'].forEach(x => n.delete(x)); return n; });
    neuLaden();
    zeige(meldung);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setParams, neuLaden, zeige]);
  const panelUmschalten = (p: 'import' | 'export') => {
    if (formularOffen) { if (formularGeaendert) { setVerwerfen(true); return; } schliesseFormular(); }
    setListenPanel(akt => (akt === p ? null : p));
  };

  const importObjekte = useMemo<ObjektKurz[]>(() => (daten?.objekte ?? []).map(o => ({
    id: o.id, bezeichnung: o.bezeichnung ?? null, strasse: o.strasse ?? null, hausnummer: o.hausnummer ?? null, stadt: o.stadt ?? o.ort ?? null, objektnummer: o.objektnummer ?? null,
  })), [daten]);
  const exportRoh = useMemo(() => new Map<string, EigExportRoh>((daten?.eigentuemer ?? []).map(e => [e.id, e])), [daten]);

  const datum = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <header className="flex items-end justify-between flex-wrap" style={{ gap: 14 }}>
        <div className="flex flex-col" style={{ gap: 2 }}>
          <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>{datum}</span>
          <h1 className="m-0" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.035em', lineHeight: 1.05 }}>Eigentümer</h1>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>
            {daten ? `${k.aktiv} aktive Eigentümer · ${k.objekte} Objekte · ${k.einheiten} Einheiten` : 'Wird geladen …'}
          </span>
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={40} icon={FileCsv} onClick={() => panelUmschalten('import')} disabled={!daten} aria-expanded={listenPanel === 'import'} style={KOPF_STIL}>CSV-Import</ImmoButton>
          <ImmoButton groesse={40} icon={Export} onClick={() => panelUmschalten('export')} disabled={!daten} aria-expanded={listenPanel === 'export'} style={KOPF_STIL}>Export</ImmoButton>
          <ImmoButton art={formularOffen ? 'fuellung' : 'akzent'} groesse={40} icon={Plus} iconGedreht={formularOffen} aria-expanded={formularOffen}
            onClick={neuerEigentuemer} disabled={!daten} style={{ padding: '0 16px', ...(formularOffen ? { fontWeight: 800 } : {}) }}>
            {formularOffen ? 'Abbrechen' : 'Neuer Eigentümer'}
          </ImmoButton>
        </div>
      </header>

      {zustand.status === 'fehler' && (
        <div role="alert" className="immo-glas flex items-center flex-wrap" style={{ borderRadius: 26, padding: 20, gap: 14 }}>
          <WarningCircle weight="fill" size={28} color="var(--bad)" />
          <div className="flex flex-col flex-1" style={{ minWidth: 200 }}>
            <span style={{ fontSize: 15, fontWeight: 800 }}>Eigentümer konnten nicht geladen werden</span>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{zustand.meldung}</span>
          </div>
          <ImmoButton icon={ArrowClockwise} onClick={neuLaden}>Erneut versuchen</ImmoButton>
        </div>
      )}

      {daten && listenPanel === 'import' && (
        <ImportPanel bestehende={daten.eigentuemer} objekte={importObjekte} onSchliessen={() => setListenPanel(null)} onImportiert={neuLaden} />
      )}
      {daten && listenPanel === 'export' && (
        <ExportPanel ansicht={sichtbar} alle={zeilen} roh={exportRoh} heute={heute} onSchliessen={() => setListenPanel(null)} />
      )}

      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}

      {daten && (
        <FlipCard
          gedreht={formularOffen}
          vorne={(
            <EigentuemerWidgets k={k} wechsel={wechsel}
              onAktiv={() => setzeParam('f', 'aktiv', 'aktiv')} onSaldo={() => setzeParam('f', 'saldo', 'aktiv')} onEigentuemer={oeffne} />
          )}
          hinten={formularOffen ? (
            <NeuerEigentuemerFormular daten={daten} objekte={optionen} heute={heute} onGeaendert={setFormularGeaendert} onGespeichert={gespeichert} />
          ) : null}
        />
      )}
      {verwerfen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die Eingaben für den neuen Eigentümer gehen verloren."
          bestaetigenLabel="Verwerfen" icon={ArrowCounterClockwise} onBestaetigen={schliesseFormular} onAbbrechen={() => setVerwerfen(false)} />
      )}

      <PortalSchalterKarte art="eigentuemerportal" onMeldung={zeige} />
      <BenachrichtigungKarte onMeldung={zeige} />

      <div ref={listeOben} className="flex flex-wrap items-center" style={{ gap: 10, scrollMarginTop: 16 }}>
        <ListenSuche wert={suche} onChange={sucheAendern} platzhalter="Name, Firma, Objekt, Ort …" ariaLabel="Eigentümer durchsuchen" />
        <SegmentedControl art="akzent" ariaLabel="Status" wert={status} onChange={f => setzeParam('f', f, 'aktiv')}
          segmente={STATUS_FILTER.map(f => ({ key: f.key, label: `${f.label} · ${z.status[f.key]}` }))} />
        <SegmentedControl art="akzent" ariaLabel="Rolle" wert={rolle} onChange={r => setzeParam('rf', r, 'alle')}
          segmente={ROLLEN_FILTER.map(f => ({ key: f.key, label: `${f.label} · ${z.rollen[f.key]}` }))} />
        <ObjektFilter optionen={optionen} wert={objekt} onChange={id => setzeParam('obj', id, 'alle')} alleAnzahl={alleAnzahl} />
        <AnsichtUmschalter wert={ansicht} onChange={a => setzeParam('view', a, 'kacheln')} />
      </div>

      {zustand.status === 'laedt' && (
        <section aria-busy="true" aria-label="Eigentümer werden geladen" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="animate-pulse" style={{ height: 176, borderRadius: 26, background: 'var(--fill2)' }} />)}
        </section>
      )}

      {daten && sichtbar.length === 0 && (
        <div className="immo-glas flex flex-col items-center text-center" style={{ borderRadius: 26, padding: 34, gap: 10, fontSize: 14, color: 'var(--ink3)' }}>
          {zeilen.length === 0 ? 'Noch keine Eigentümer angelegt' : 'Keine Eigentümer unter diesen Kriterien gefunden'}
          {(suche || objekt !== 'alle' || status !== 'aktiv' || rolle !== 'alle') && zeilen.length > 0 && (
            <ImmoButton onClick={() => { setSuche(''); setParams(p => { const n = new URLSearchParams(p); ['f', 'rf', 'obj', 'seite'].forEach(x => n.delete(x)); return n; }); }}>
              Filter zurücksetzen
            </ImmoButton>
          )}
        </div>
      )}

      {daten && sichtbar.length > 0 && ansicht === 'kacheln' && (
        <section aria-label="Eigentümer" className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 14 }}>
          {seite.eintraege.map(x => <EigentuemerKachel key={x.id} z={x} onOeffnen={oeffne} />)}
        </section>
      )}
      {daten && sichtbar.length > 0 && ansicht === 'liste' && <EigentuemerTabelle zeilen={seite.eintraege} onOeffnen={oeffne} />}
      {daten && sichtbar.length > 0 && (
        <Seitennavigation seite={seite.seite} seiten={seite.seiten} von={seite.von} bis={seite.bis} gesamt={seite.gesamt} einheit="Eigentümer" onSeite={zuSeite} />
      )}
    </>
  );
}
